/**
 * Green contours for the .pocketgolf v2 format.
 *
 * A v2 hole may carry a `heights` array alongside its `grid`: one string per row, one character per
 * cell, `0`–`9`. Only cells that are green or the cup are read; everything else in the matrix is
 * ignored, which is why the rest of a row can be zeros.
 *
 * Heights rather than slope vectors, because a height field cannot describe an impossible green. A
 * slope field can be authored so every cell drains into its neighbour in a loop, and a ball on it
 * would roll forever without ever losing elevation. Only *differences* matter, so a green written
 * `3 4 5 5` plays exactly like one written `0 1 2 2`.
 *
 * The game's copy of this arithmetic is Sources/PocketGolfCore/GreenContour.swift. The two must
 * agree, or the arrows drawn here point somewhere the ball does not go — which is worse than drawing
 * no arrows at all. Keep them in sync.
 */

import { DEFAULT_WIDTH, gridWidth } from './grid.js';

export const MIN_HEIGHT = 0;
export const MAX_HEIGHT = 9;

/** Tiles a putt rolls across. The cup is included so a green has no hole in its contour. */
const PUTTING_SURFACE_CHARS = ['G', '0'];

export function isPuttingSurface(char) {
	return PUTTING_SURFACE_CHARS.includes(char);
}

/** A heights matrix of all zeros, shaped to a grid. */
export function emptyHeights(rows, width = DEFAULT_WIDTH) {
	return Array.from({ length: rows }, () => '0'.repeat(width));
}

/**
 * A heights matrix shaped to the given grid.
 *
 * Rows and characters that are missing read as 0 and rows past the end of the grid are dropped — the
 * same forgiving treatment the game's parser gives `grid` itself, so a hand-edited file that is a few
 * characters short still loads rather than failing.
 */
export function normaliseHeights(heights, grid) {
	const source = Array.isArray(heights) ? heights : [];
	const width = gridWidth(grid);
	return grid.map((_, y) => {
		const row = typeof source[y] === 'string' ? source[y] : '';
		let out = '';
		for (let x = 0; x < width; x++) {
			out += String(clampHeight(parseHeight(row.charAt(x))));
		}
		return out;
	});
}

function parseHeight(char) {
	const value = parseInt(char, 10);
	return Number.isFinite(value) ? value : 0;
}

export function clampHeight(value) {
	return Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, value));
}

/** The height at a cell, or `null` where the cell is not a putting surface. */
export function heightAt(grid, heights, x, y) {
	if (y < 0 || y >= grid.length || x < 0 || x >= gridWidth(grid)) return null;
	if (!isPuttingSurface(grid[y].charAt(x))) return null;
	const row = heights[y] || '';
	return clampHeight(parseHeight(row.charAt(x)));
}

/**
 * Which way the surface falls away under a cell, and how steeply.
 *
 * Central differences, with any neighbour that is off the surface substituted by the cell itself. That
 * substitution is load-bearing: a green sitting at height 4 in a matrix whose default is 0 would
 * otherwise have a four-step cliff all the way round its edge, and a putt near the fringe would be
 * flung sideways by terrain nobody drew. It also means the gradient tapers to nothing at the edge of
 * the green rather than pointing off it.
 *
 * `dx`/`dy` point **downhill** in grid space, in height steps per tile. `angle` is the game's aim
 * convention: degrees, 0 = north, increasing clockwise.
 */
export function slopeAt(grid, heights, x, y) {
	const centre = heightAt(grid, heights, x, y);
	if (centre === null) return null;

	const sample = (dx, dy) => {
		const value = heightAt(grid, heights, x + dx, y + dy);
		return value === null ? centre : value;
	};

	// Uphill gradient, then negated: the slope points the way the ball is pulled.
	const dx = -(sample(1, 0) - sample(-1, 0)) / 2;
	const dy = -(sample(0, 1) - sample(0, -1)) / 2;
	const steepness = Math.sqrt(dx * dx + dy * dy);
	let angle = (Math.atan2(dx, -dy) * 180) / Math.PI;
	if (angle < 0) angle += 360;

	return { dx, dy, steepness, angle };
}

/** The lowest and highest step on the putting surface, or `null` if there is no surface. */
export function heightRange(grid, heights) {
	let low = Infinity;
	let high = -Infinity;
	const width = gridWidth(grid);
	for (let y = 0; y < grid.length; y++) {
		for (let x = 0; x < width; x++) {
			const value = heightAt(grid, heights, x, y);
			if (value === null) continue;
			low = Math.min(low, value);
			high = Math.max(high, value);
		}
	}
	return low <= high ? { low, high } : null;
}

/**
 * Whether this hole has a contour worth exporting.
 *
 * A dead-flat green is indistinguishable from no green contour at all, and the game treats it as none —
 * so a matrix of identical numbers is dropped rather than written out, which keeps a file from claiming
 * a feature it does not use.
 */
export function hasContour(grid, heights) {
	const range = heightRange(grid, heights);
	return range !== null && range.low < range.high;
}

/** How steep a slope is, in the same words the game's HUD uses. */
export function slopeGrade(steepness) {
	if (steepness < 0.3) return 'Flat';
	if (steepness < 0.75) return 'Gentle';
	if (steepness < 1.25) return 'Moderate';
	return 'Severe';
}
